import { Box, Flex, IconButton, Input, List, ListItem, Spinner, Text } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { Icon } from '../Icon'
import { useGoogleMaps } from './GoogleMapsProvider'

export interface IParsedPlace {
  street: string
  number: string
  neighborhood: string
  city: string
  state: string
  zipcode: string
  latitude: number | null
  longitude: number | null
  place_id: string | null
  place_name: string | null
}

interface IProps {
  onPlaceSelected: (place: IParsedPlace) => void
  disabled?: boolean
}

function parseAddressComponents(
  components: google.maps.GeocoderAddressComponent[]
): Omit<IParsedPlace, 'latitude' | 'longitude' | 'place_id' | 'place_name'> {
  const result = {
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    zipcode: ''
  }

  for (const component of components) {
    const types = component.types

    if (types.includes('route')) {
      result.street = component.long_name
    } else if (types.includes('street_number')) {
      result.number = component.long_name
    } else if (types.includes('sublocality_level_1') || types.includes('sublocality')) {
      result.neighborhood = component.long_name
    } else if (types.includes('neighborhood') && !result.neighborhood) {
      result.neighborhood = component.long_name
    } else if (types.includes('administrative_area_level_2')) {
      result.city = component.long_name
    } else if (types.includes('administrative_area_level_1')) {
      result.state = component.short_name
    } else if (types.includes('postal_code')) {
      result.zipcode = component.long_name.replace('-', '')
    }
  }

  return result
}

export function AddressAutocomplete({ onPlaceSelected, disabled }: IProps) {
  const { isLoaded } = useGoogleMaps()
  const [query, setQuery] = useState('')
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null)
  // Reused across the prediction + details calls of a single search so Google bills them together.
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService()
    }
  }, [isLoaded])

  // Close the predictions dropdown when clicking outside the component.
  useEffect(() => {
    if (!open) return
    const handler = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Fetch predictions only on demand (button/Enter) instead of on every keystroke.
  const handleSearch = () => {
    const input = query.trim()
    if (!input || !autocompleteServiceRef.current) return

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()
    }

    setLoading(true)
    autocompleteServiceRef.current.getPlacePredictions(
      { input, componentRestrictions: { country: 'br' }, sessionToken: sessionTokenRef.current },
      (results, status) => {
        setLoading(false)
        setOpen(true)
        setPredictions(status === google.maps.places.PlacesServiceStatus.OK && results ? results : [])
      }
    )
  }

  const handleSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesServiceRef.current) {
      placesServiceRef.current = new google.maps.places.PlacesService(document.createElement('div'))
    }

    setOpen(false)
    setQuery(prediction.description)

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'geometry', 'name', 'place_id'],
        sessionToken: sessionTokenRef.current ?? undefined
      },
      (place, status) => {
        sessionTokenRef.current = null // ends the billing session
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.address_components) return

        const parsed = parseAddressComponents(place.address_components)
        const location = place.geometry?.location

        onPlaceSelected({
          ...parsed,
          latitude: location ? location.lat() : null,
          longitude: location ? location.lng() : null,
          place_id: place.place_id ?? null,
          place_name: place.name ?? null
        })
      }
    )
  }

  if (!isLoaded) {
    return <Input placeholder="Carregando..." disabled />
  }

  return (
    <Box position="relative" ref={containerRef}>
      <Flex gap={2}>
        <Input
          value={query}
          placeholder="Ex: Shopping São Paulo, Rua Augusta 100..."
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleSearch()
            }
          }}
          isDisabled={disabled}
        />
        <IconButton
          aria-label="Buscar endereço"
          icon={loading ? <Spinner size="sm" /> : <Icon name="lni lni-search-alt" />}
          onClick={handleSearch}
          isDisabled={disabled || !query.trim()}
        />
      </Flex>

      {open && (
        <Box
          position="absolute"
          zIndex={20}
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          borderWidth="1px"
          borderRadius="md"
          boxShadow="md"
          maxH="240px"
          overflowY="auto"
        >
          {predictions.length === 0 ? (
            <Text variant="caption" px={3} py={2}>
              Nenhum resultado encontrado
            </Text>
          ) : (
            <List>
              {predictions.map((prediction) => (
                <ListItem
                  key={prediction.place_id}
                  px={3}
                  py={2}
                  fontSize="sm"
                  cursor="pointer"
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => handleSelect(prediction)}
                >
                  {prediction.description}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  )
}
